import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/pfc";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success("Welcome to PFC");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inp = "w-full bg-ink-700 border border-white/10 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none focus:ring-1 focus:ring-pfc-gold";

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1613991917190-e68d404b2617?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwbW9kZWwlMjBwb3J0cmFpdCUyMGRhcmt8ZW58MHx8fHwxNzgxMjI3MDUxfDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="label text-pfc-gold mb-2">Join The Campaign</div>
          <p className="font-display text-5xl text-white uppercase leading-[0.9]">
            Your <span className="gold-text">Story</span><br /> Belongs<br /> On The <span className="silver-text">Cover.</span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <img src={LOGO_URL} alt="PFC" className="h-16 w-16 mb-6 object-contain" />
          <h1 className="font-display text-3xl lg:text-4xl text-white uppercase">Create <span className="gold-text">Account</span></h1>
          <p className="text-white/60 mt-2 font-body">Submit stories, manage your profile, and stay connected.</p>

          <form onSubmit={submit} className="space-y-4 mt-8" data-testid="register-form">
            <div>
              <label className="label text-white/70 mb-2 block">Full Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="register-name" />
            </div>
            <div>
              <label className="label text-white/70 mb-2 block">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="register-email" />
            </div>
            <div>
              <label className="label text-white/70 mb-2 block">Password</label>
              <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} data-testid="register-password" />
            </div>
            <button type="submit" disabled={loading} className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50" data-testid="register-submit">
              {loading ? "Creating…" : "Create Account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="label text-white/40">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button onClick={startGoogle} className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold transition" data-testid="register-google">
            Continue with Google
          </button>

          <p className="text-white/60 mt-6 font-body text-sm text-center">
            Already have an account? <Link to="/login" className="text-pfc-gold hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
